import React, { use, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './styles.module.scss';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import ChipList from '@components-clean/core/chip-list';
import { InfoField } from '@components-clean/core/info-field';
import Tooltip from '@components-clean/core/tooltip';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { RiskMonitoringTab } from '@pages-clean/risk-assessment/constants';
import { ExtendedCompleteRiskFields } from '@pages-clean/risk-assessment/risk-log/constants';
import AddObservationDialog, {
    RiskObservationFormInputs,
} from '@pages-clean/risk-assessment/risk-log/dialogs/add-observation-dialog';
import DeleteObservationDialog from '@pages-clean/risk-assessment/risk-log/dialogs/delete-observation-dialog';
import RestoreObservationDialog from '@pages-clean/risk-assessment/risk-log/dialogs/restore-observation-dialog';
import { RiskMonitoringActionChipsValues } from '@pages-clean/risk-assessment/risk-monitoring/constants';
import { RiskMonitoringContext } from '@pages-clean/risk-assessment/risk-monitoring/interfaces/context';
import ObservationArea from '@pages-clean/risk-assessment/risk-monitoring/manage-actions/childArea/observation/observation';
import RiskArea from '@pages-clean/risk-assessment/shared/risk/risk';
import { CountryInputType } from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromLocal } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { saveRiskObservation, saveRiskObservationVariables } from '@queries/generated/risk-assessment.generated';
import { GET_RISK_OBSERVATIONS_AND_ACTIONS_AND_RISKS, SAVE_RISK_OBSERVATION } from '@queries/risk-assessment';

const ActionChildAreas = () => {
    const [chip, setChip] = useState<RiskMonitoringActionChipsValues>(RiskMonitoringActionChipsValues.Observation);
    const { t }: { t: (key: string) => string } = useTranslation('ram', {
        keyPrefix: 'riskMonitoring',
    });
    const user = useAuthorization();
    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });
    const { t: tCommon } = useTranslation('common');
    const navigate = useNavigate();
    const { current } = use(RiskMonitoringContext);
    const { selectedObservation, selectedObservationRisk, observationCount, study, actionCount } = current;
    const showAddObservationDialog = useBoolean(false);
    const showDeleteObservationDialog = useBoolean(false);
    const showRestoreObservationDialog = useBoolean(false);

    const [saveObservationMutation] = useMutation<saveRiskObservation, saveRiskObservationVariables>(
        SAVE_RISK_OBSERVATION,
    );

    const onAddObservationClose = () => {
        showAddObservationDialog.setFalse();
    };

    const onSubmitSaveObservation = async (addObservationData: RiskObservationFormInputs) => {
        saveObservationMutation({
            variables: {
                observation: {
                    id: selectedObservation?.id,
                    studyId: study?.id,
                    riskId: selectedObservationRisk?.id,
                    description: addObservationData.description,
                    classificationId: addObservationData.classification?.id,
                    vendorIds: addObservationData.vendors?.map((v) => v.id) ?? [],
                    countries: addObservationData.countries?.map((c) => c as CountryInputType) ?? [],
                    responsibleFunctionId: addObservationData.responsibleFunction?.id,
                    ownerId: addObservationData.owner?.id,
                    potentialCause: addObservationData.potentialCause,
                    confirmedCause: addObservationData.confirmedCause,
                    observationDate: addObservationData?.observationDate
                        ? stringDateWithoutTimeFromLocal(addObservationData.observationDate)
                        : null,
                    closedDate: addObservationData?.closedDate
                        ? stringDateWithoutTimeFromLocal(addObservationData.closedDate)
                        : null,
                    resolution: addObservationData.resolution,
                    comments: addObservationData.comments,
                },
            },
            refetchQueries: [
                {
                    query: GET_RISK_OBSERVATIONS_AND_ACTIONS_AND_RISKS,
                    variables: {
                        studyId: study?.id,
                    },
                },
            ],
        })
            .then(async (response) => {
                if (!response?.data || !response.data?.saveRiskObservation) {
                    ShowError(tCommon('errorMessage'));
                    return;
                }
                showAddObservationDialog.setFalse();
                ShowSuccess(tCommon('actionsSuccessfulToast'));
            })
            .catch(() => {
                ShowError(tCommon('errorMessage'));
            });
    };

    const onDeleteObservationClose = () => {
        showDeleteObservationDialog.setFalse();
    };

    const onSubmitDeleteObservation = async () => {
        ShowSuccess(tCommon('actionsSuccessfulToast'));
        showDeleteObservationDialog.setFalse();
    };

    const onRestoreObservationClose = () => {
        showRestoreObservationDialog.setFalse();
    };

    const onSubmitRestoreObservation = async () => {
        ShowSuccess(tCommon('actionsSuccessfulToast'));
        showRestoreObservationDialog.setFalse();
    };

    if (actionCount > 1)
        return (
            <div className="k-h-full">
                <InfoField type="Info" message={t('manageActions.info.multipleRecordsSelected')} layout="center" />
            </div>
        );

    if (!selectedObservation)
        return (
            <div className="k-h-full">
                <InfoField type="Info" message={t('manageActions.info.noParentRecordSelected')} layout="center" />
            </div>
        );

    return (
        <Card
            data-testid="risk-monitoring-actions-child-area"
            style={{ height: '100%' }}
            title={
                <ChipList
                    data-testid="risk-monitoring-manage-actions-chip-list"
                    data={[
                        {
                            text: t('manageActions.chips.observation'),
                            value: RiskMonitoringActionChipsValues.Observation,
                        },
                        {
                            text: t('manageActions.chips.risk'),
                            value: RiskMonitoringActionChipsValues.Risk,
                        },
                    ]}
                    value={chip}
                    onChange={(event) => {
                        const selectedChip = event.value as RiskMonitoringActionChipsValues;
                        if (selectedChip) setChip(selectedChip);
                    }}
                />
            }
            headerActions={
                selectedObservation ? (
                    chip === RiskMonitoringActionChipsValues.Risk ? (
                        <Tooltip parentTitle>
                            <button
                                aria-label={t('manageActions.info.goToRisk')}
                                data-test-id="manage-actions-go-to-risk-button"
                                type="button"
                                title={t('manageActions.info.goToRisk')}
                                className={styles.iconButtons}
                                onClick={() => {
                                    NavigationService.setViewRiskDetails();
                                    // Cannot use AllRoutes dictionary due to circular dependency.
                                    navigate({
                                        pathname: `/risk-assessment/risk-log/id/${selectedObservationRisk?.recordId}`,
                                    });
                                }}
                            >
                                <FontAwesomeIcon icon={['far', 'arrow-up-right-from-square']} />
                            </button>
                        </Tooltip>
                    ) : (
                        <div style={{ display: 'flex', fontSize: '16px' }}>
                            {selectedObservation.deleted ? (
                                <Tooltip parentTitle position="left">
                                    <button
                                        aria-label={t('manageActions.info.restore')}
                                        data-test-id="manage-actions-restore-observation-button"
                                        type="button"
                                        title={t('manageActions.info.restore')}
                                        className={styles.iconButtons}
                                        disabled={!canEdit}
                                        onClick={() => {
                                            showRestoreObservationDialog.setTrue();
                                        }}
                                    >
                                        <FontAwesomeIcon icon={['far', 'arrow-rotate-backward']} />
                                    </button>
                                </Tooltip>
                            ) : (
                                <Tooltip parentTitle position="left">
                                    <button
                                        aria-label={t('manageActions.info.delete')}
                                        data-test-id="manage-actions-delete-observation-button"
                                        disabled={!canEdit}
                                        type="button"
                                        title={t('manageActions.info.delete')}
                                        className={styles.iconButtons}
                                        onClick={() => {
                                            showDeleteObservationDialog.setTrue();
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={['fas', 'trash-xmark']}
                                            className={styles.redIcon}
                                            opacity={canEdit ? 1 : 0.5}
                                        />
                                    </button>
                                </Tooltip>
                            )}
                            {!selectedObservation.deleted && (
                                <Button
                                    primary
                                    data-testid="rm-manage-actions-edit-observation-button"
                                    disabled={!canEdit}
                                    style={{ marginLeft: '8px' }}
                                    onClick={() => {
                                        showAddObservationDialog.setTrue();
                                    }}
                                >
                                    {t('manageActions.buttons.edit')}
                                </Button>
                            )}
                        </div>
                    )
                ) : undefined
            }
        >
            <div style={{ height: '100%' }} className="remove-card-padding">
                {chip === RiskMonitoringActionChipsValues.Observation && (
                    <>
                        <ObservationArea
                            selectedObservation={selectedObservation}
                            parent={RiskMonitoringTab.ManageActions}
                        />
                        {showAddObservationDialog.value && (
                            <AddObservationDialog
                                onClose={onAddObservationClose}
                                onSubmit={onSubmitSaveObservation}
                                studyRef={study?.id}
                                observation={selectedObservation}
                                risk={selectedObservationRisk! as unknown as ExtendedCompleteRiskFields}
                            />
                        )}
                        {showDeleteObservationDialog.value && selectedObservation && selectedObservationRisk && (
                            <DeleteObservationDialog
                                onClose={onDeleteObservationClose}
                                onSubmit={onSubmitDeleteObservation}
                                observation={selectedObservation}
                                studyId={study?.id}
                                riskId={selectedObservationRisk?.id}
                            />
                        )}
                        {showRestoreObservationDialog.value && selectedObservation && selectedObservationRisk && (
                            <RestoreObservationDialog
                                onClose={onRestoreObservationClose}
                                onSubmit={onSubmitRestoreObservation}
                                observation={selectedObservation}
                                studyId={study?.id}
                                riskId={selectedObservationRisk?.id}
                            />
                        )}
                    </>
                )}
                {chip === RiskMonitoringActionChipsValues.Risk && (
                    <RiskArea
                        selectedObservation={selectedObservation}
                        observationIdCount={observationCount}
                        parent={RiskMonitoringTab.ManageActions}
                    />
                )}
            </div>
        </Card>
    );
};

export default ActionChildAreas;
